/**
 * Minimal single-image PDF writer.
 *
 * We only ever need to wrap one full-bleed raster page, so pulling in a full
 * PDF library (jsPDF et al) would cost ~100KB gzipped for a feature we can
 * express in a handful of objects. This builds the PDF byte-for-byte instead.
 *
 * Encoding preference:
 *   1. /FlateDecode over raw RGB — lossless, which matters because JPEG
 *      ringing around QR module edges hurts scan reliability.
 *   2. /DCTDecode (JPEG) as a fallback where CompressionStream is missing.
 */

// A4 in PostScript points (72pt = 1in; 210mm x 297mm).
export const A4_PT_WIDTH = 595.276;
export const A4_PT_HEIGHT = 841.89;

const enc = (s: string) => new TextEncoder().encode(s);

/**
 * zlib-compress a byte array. `CompressionStream('deflate')` emits the zlib
 * container (RFC 1950) that PDF's /FlateDecode expects — not raw deflate.
 * Returns null when the API is unavailable so callers can fall back.
 */
async function deflate(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    // Written through the writable side rather than Blob.stream() so this
    // works anywhere CompressionStream does, without a Blob dependency.
    const cs = new CompressionStream('deflate');
    const writer = cs.writable.getWriter();
    void writer.write(data);
    void writer.close();
    return new Uint8Array(await new Response(cs.readable).arrayBuffer());
  } catch {
    return null;
  }
}

/**
 * Drop the alpha channel, compositing over white. Poster canvases are opaque,
 * so this is a straight copy in practice — but a stray transparent pixel
 * would otherwise read as black once alpha is discarded.
 */
export function rgbaToRgb(rgba: Uint8ClampedArray): Uint8Array<ArrayBuffer> {
  const pixels = rgba.length / 4;
  const rgb = new Uint8Array(pixels * 3);
  for (let i = 0, j = 0; i < pixels; i++, j += 3) {
    const k = i * 4;
    const a = rgba[k + 3] / 255;
    if (a === 1) {
      rgb[j] = rgba[k];
      rgb[j + 1] = rgba[k + 1];
      rgb[j + 2] = rgba[k + 2];
    } else {
      rgb[j] = Math.round(rgba[k] * a + 255 * (1 - a));
      rgb[j + 1] = Math.round(rgba[k + 1] * a + 255 * (1 - a));
      rgb[j + 2] = Math.round(rgba[k + 2] * a + 255 * (1 - a));
    }
  }
  return rgb;
}

interface ImagePayload {
  bytes: Uint8Array;
  /** PDF filter name, e.g. '/FlateDecode'. */
  filter: string;
}

async function encodeImage(canvas: HTMLCanvasElement): Promise<ImagePayload> {
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const flate = await deflate(rgbaToRgb(data));
    if (flate) return { bytes: flate, filter: '/FlateDecode' };
  }

  // Fallback: hand the raw JPEG bitstream straight to /DCTDecode.
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.95),
  );
  if (!blob) throw new Error('Could not encode poster image');
  return { bytes: new Uint8Array(await blob.arrayBuffer()), filter: '/DCTDecode' };
}

/**
 * Wrap a canvas as a single full-bleed A4 portrait page.
 *
 * The canvas is stretched to the full MediaBox, so its aspect ratio should
 * already be A4 (1:√2) or the page will look squashed.
 */
export async function canvasToPdfBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const { bytes, filter } = await encodeImage(canvas);
  const { width, height } = canvas;

  const chunks: Uint8Array[] = [];
  let length = 0;
  const push = (chunk: Uint8Array | string) => {
    const b = typeof chunk === 'string' ? enc(chunk) : chunk;
    chunks.push(b);
    length += b.length;
  };

  // offsets[n] = byte offset of object n. Index 0 is the free-list head.
  const offsets: number[] = [];
  const beginObj = (n: number) => {
    offsets[n] = length;
    push(`${n} 0 obj\n`);
  };

  push('%PDF-1.4\n');
  // Binary comment marks the file as containing 8-bit data.
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  beginObj(1);
  push('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  beginObj(2);
  push('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

  beginObj(3);
  push(
    '<< /Type /Page /Parent 2 0 R ' +
      `/MediaBox [0 0 ${A4_PT_WIDTH} ${A4_PT_HEIGHT}] ` +
      '/Resources << /XObject << /Im0 5 0 R >> >> ' +
      '/Contents 4 0 R >>\nendobj\n',
  );

  // Content stream: scale the unit image square up to the full page.
  const content = `q ${A4_PT_WIDTH} 0 0 ${A4_PT_HEIGHT} 0 0 cm /Im0 Do Q\n`;
  beginObj(4);
  push(`<< /Length ${enc(content).length} >>\nstream\n`);
  push(content);
  push('endstream\nendobj\n');

  beginObj(5);
  push(
    '<< /Type /XObject /Subtype /Image ' +
      `/Width ${width} /Height ${height} ` +
      '/ColorSpace /DeviceRGB /BitsPerComponent 8 ' +
      `/Filter ${filter} /Length ${bytes.length} >>\nstream\n`,
  );
  push(bytes);
  push('\nendstream\nendobj\n');

  // ── Cross-reference table ──────────────────────────────────────────────
  const objCount = 6; // objects 1..5 plus the free-list head
  const xrefOffset = length;
  push(`xref\n0 ${objCount}\n`);
  push('0000000000 65535 f \n');
  for (let n = 1; n < objCount; n++) {
    // Each entry must be exactly 20 bytes.
    push(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  }
  push(`trailer\n<< /Size ${objCount} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`);

  return new Blob(chunks as BlobPart[], { type: 'application/pdf' });
}
